import { required, useRecordContext, useTranslate } from "ra-core";
import { ReferenceInput } from "@/components/admin/reference-input";
import { TextInput } from "@/components/admin/text-input";
import { SelectInput } from "@/components/admin/select-input";
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/use-mobile";

import { MoreFields } from "../misc/MoreFields";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Company, Sale } from "../types";

const isUrl = (url: string) => {
  if (!url) return;
  // letters of any script, so that Cyrillic domains such as завод.рф pass
  const UrlRegex = new RegExp(
    /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[\p{L}0-9]+([-.]{1}[\p{L}0-9]+)*\.[\p{L}]{2,24}(:[0-9]{1,5})?(\/.*)?$/iu,
  );
  if (!UrlRegex.test(url)) {
    return {
      message: "crm.validation.invalid_url",
      args: { _: "Must be a valid URL" },
    };
  }
};

/**
 * The company form keeps only what a sales manager fills in (owner's rule
 * 04.11): name, phone, website, city, tax id (ИНН), account manager, comment.
 * Address and sector sit under «Ещё». Size, revenue, LinkedIn, logo, context
 * links, zip code, state and country are gone; their columns stay.
 */
export const CompanyInputs = () => {
  const isMobile = useIsMobile();
  const translate = useTranslate();
  const record = useRecordContext<Company>();

  return (
    <div className="flex flex-col gap-6 p-1">
      <TextInput
        source="name"
        className="w-full"
        validate={required()}
        helperText={false}
        placeholder={translate("resources.companies.fields.name", {
          _: "Company name",
        })}
      />
      <div className={`flex gap-6 ${isMobile ? "flex-col" : "flex-row"}`}>
        <div className="flex flex-col gap-4 flex-1">
          <TextInput source="phone_number" type="tel" helperText={false} />
          <TextInput source="website" helperText={false} validate={isUrl} />
          <TextInput source="city" helperText={false} />
          <TextInput source="tax_identifier" helperText={false} />
        </div>
        <Separator orientation={isMobile ? "horizontal" : "vertical"} />
        <div className="flex flex-col gap-4 flex-1">
          <ReferenceInput
            source="sales_id"
            reference="sales"
            filter={{
              "disabled@neq": true,
            }}
          >
            <SelectInput helperText={false} optionText={saleOptionRenderer} />
          </ReferenceInput>
          <TextInput source="description" multiline helperText={false} />
        </div>
      </div>
      <MoreFields defaultOpen={!!(record?.address || record?.sector)}>
        <CompanyMoreInputs />
      </MoreFields>
    </div>
  );
};

const CompanyMoreInputs = () => {
  const { companySectors } = useConfigurationContext();
  return (
    <div className={`flex gap-6 flex-col md:flex-row`}>
      <TextInput source="address" helperText={false} className="flex-1" />
      <SelectInput
        source="sector"
        choices={companySectors}
        optionText="label"
        optionValue="value"
        helperText={false}
        className="flex-1"
      />
    </div>
  );
};

const saleOptionRenderer = (choice: Sale) =>
  `${choice.first_name} ${choice.last_name}`;
